import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateUserMutation } from "../usersApiSlice";
import { UserObject } from "../../../shared/types";

type Props = {
  user: UserObject;
};

const NAME_REGEX = /^[a-zA-Z-,]+(\s{0,1}[a-zA-Z-, ])*$/;
const EMAIL_REGEX = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/;

const labelClass =
  "mb-[7px] block text-[12px] font-bold tracking-[0.04em] text-muted";

/** Profile panel — real name/email/photo update via updateMe (Multer/Sharp handle the photo). */
const UpdateUserInfo = ({ user }: Props) => {
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const navigate = useNavigate();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [photo, setPhoto] = useState<File | undefined>(undefined);
  const [errMsg, setErrMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const avatar = `${process.env.REACT_APP_API_URL}/img/users/${
    user.photo ? user.photo : "default.jpg"
  }`;
  const preview = photo ? URL.createObjectURL(photo) : avatar;
  const dirty = name !== user.name || email !== user.email || Boolean(photo);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = new FormData();
    if (NAME_REGEX.test(name) && name !== user.name) data.append("name", name);
    if (EMAIL_REGEX.test(email) && email !== user.email)
      data.append("email", email);
    if (photo) data.append("photo", photo);

    if (Array.from(data.keys()).length === 0) {
      setErrMsg(
        "Nothing to update — or the name/email entered isn't valid. Please check and try again."
      );
      return;
    }

    setErrMsg("");
    try {
      await updateUser(data).unwrap();
      navigate(0);
    } catch {
      setErrMsg("Couldn't update your profile. Please try again.");
    }
  };

  const handleCancel = () => {
    setName(user.name);
    setEmail(user.email);
    setPhoto(undefined);
    setErrMsg("");
  };

  return (
    <div className="rounded-[20px] bg-white p-[30px] shadow-[0_14px_30px_-22px_rgba(0,0,0,0.25)]">
      <h2 className="mb-1 font-serif text-[26px] font-normal text-ink">
        Profile details
      </h2>
      <p className="mb-[26px] text-[14px] text-muted-300">
        Update your photo and personal information.
      </p>

      <form onSubmit={handleSave}>
        {/* Avatar row */}
        <div className="mb-7 flex items-center gap-[18px] border-b border-line-100 pb-[26px]">
          <img
            src={preview}
            alt={user.name}
            crossOrigin={photo ? undefined : "anonymous"}
            className="h-20 w-20 rounded-full object-cover"
          />
          <div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded-[10px] bg-secondary-400 px-[18px] py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-ink"
            >
              {photo ? "Change photo" : "Upload new photo"}
            </button>
            <div className="mt-2 text-[12.5px] text-muted-300">
              {photo ? photo.name : "JPG or PNG, max 2MB."}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setPhoto(e.target.files?.[0])}
            />
          </div>
        </div>

        {/* Fields */}
        <div className="mb-6 grid gap-[18px] sm:grid-cols-2">
          <div>
            <label htmlFor="acct-name" className={labelClass}>
              FULL NAME
            </label>
            <input
              id="acct-name"
              className="wn-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="acct-email" className={labelClass}>
              EMAIL
            </label>
            <input
              id="acct-email"
              type="email"
              className="wn-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        {errMsg && (
          <div className="mb-4 rounded-[11px] bg-hard/10 px-3.5 py-3 text-[13.5px] font-semibold text-hard">
            {errMsg}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!dirty || isLoading}
            className="rounded-xl bg-secondary-400 px-[26px] py-3 text-[15px] font-bold text-white transition-colors hover:bg-ink disabled:opacity-50"
          >
            {isLoading ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={!dirty || isLoading}
            className="rounded-xl border border-line px-[26px] py-3 text-[15px] font-semibold text-ink transition-colors hover:border-secondary-400 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateUserInfo;
